import { type ReactNode, useEffect, useRef } from "react";
import {
  Root as DialogRoot,
  Trigger as DialogTrigger,
  Portal as DialogPortal,
  Overlay as DialogOverlay,
  Content as DialogContent,
  Title as DialogTitle,
  Description as DialogDescription,
} from "@radix-ui/react-dialog";
import { cn } from "../../lib/cn";

// Literal max-width classes — Tailwind can't see interpolated strings.
const DIALOG_SIZE = {
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
} as const;

interface DialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  trigger?: ReactNode;
  size?: keyof typeof DIALOG_SIZE;
  children: ReactNode;
}

/**
 * Shared Radix dialog chrome: overlay + centered content sheet + title.
 * Entrance is opacity-only (scale would fight the -translate-x/y-1/2 centering).
 *
 * The sheet is the landing's glass card (docs/v2-core/STYLE.md): generous
 * rounding, a soft sky wash at the top fading to white (so body copy sits on
 * white), a pastel-tinted veil behind it, and a 1rem margin on a phone.
 *
 * Stacking: overlay and sheet share one z-index, so a dialog opened on top of
 * another (a ConfirmDialog over the Active links popup) is painted after it in
 * DOM order and its veil dims the dialog underneath. Esc and outside clicks
 * close only the top one (Radix keeps a layer stack).
 *
 * Focus return: on close, focus goes back to whatever had it when the dialog
 * opened. (Radix on its own only returns focus to a <Dialog.Trigger>, and
 * most of our dialogs are opened from state with no trigger, so focus used to
 * fall to <body>.) If that element is gone by then (its row moved or was
 * removed, e.g. a link that was just cancelled), focus goes to the dialog
 * still open underneath.
 */
export function DialogShell({
  open,
  onOpenChange,
  title,
  description,
  trigger,
  size = "md",
  children,
}: DialogShellProps) {
  const opener = useRef<Element | null>(null);
  const content = useRef<HTMLDivElement | null>(null);

  // If the focused control is removed while this dialog stays open (a
  // cancelled link's button disappears once the list refreshes), the browser
  // drops focus to <body>; pull it back into the top-most open dialog. A
  // native listener, because React drops events from nodes it is unmounting.
  useEffect(() => {
    if (!open) return;
    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget) return;
      window.setTimeout(() => {
        const el = content.current;
        const lost = document.activeElement === null || document.activeElement === document.body;
        if (!lost || !el?.isConnected || el.dataset.state !== "open") return;
        const openDialogs = document.querySelectorAll('[role="dialog"][data-state="open"]');
        if (openDialogs[openDialogs.length - 1] === el) el.focus();
        // After Radix's own close-time focus move (a 0ms timer), so a dialog
        // closing on top hands focus back to its opener without a detour.
      }, 50);
    };
    document.addEventListener("focusout", onFocusOut, true);
    return () => document.removeEventListener("focusout", onFocusOut, true);
  }, [open]);

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 animate-fade-in bg-indigo-950/30 backdrop-blur-sm" />
        <DialogContent
          ref={content}
          // Radix warns when Content has no Description; opt out explicitly.
          {...(description ? {} : { "aria-describedby": undefined })}
          onOpenAutoFocus={() => {
            opener.current = document.activeElement;
          }}
          onCloseAutoFocus={(event) => {
            const target = opener.current;
            if (target instanceof HTMLElement && target.isConnected) {
              event.preventDefault();
              target.focus();
              return;
            }
            const below = document.querySelector<HTMLElement>('[role="dialog"][data-state="open"]');
            if (below) {
              event.preventDefault();
              below.focus();
            }
          }}
          className={cn(
            "fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-3rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 animate-fade-in overflow-y-auto rounded-[2rem] border border-white/80 bg-white bg-gradient-to-b from-sky-50 via-white to-white p-6 shadow-glow focus:outline-none sm:p-8",
            DIALOG_SIZE[size],
          )}
        >
          <DialogTitle className="text-balance pr-2 font-display text-[1.4rem] font-semibold leading-tight text-neutral-900 sm:text-2xl">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="mt-2 text-pretty text-sm text-neutral-600 sm:text-[15px]">
              {description}
            </DialogDescription>
          )}
          {children}
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
}
